import { CheckBoxWithLabelWidget, TabStyles } from '@tableau/widgets';
import React from 'react';

import { SessionServerSelectors, SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { AppState } from '~src/bootstrap/redux/state/AppState';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Formatters,Messages } from '~src/codegen/Localize';
import { FlowOutputStepInfo } from '~src/flow/models/FlowOutputStepInfo';
import { OutputFileFormat } from '~src/flow/serverApi/types/SubscriptionFlow';
import { classes,cssClass } from '~src/style';

import { isSelectedOutputAPublishedDatasource } from '../utils/FlowSubscriptionUtil';
import { FlowSubscriptionOutputFileFormatDropdown } from './FlowSubscriptionOutputFileFormatDropdown';
import { FlowSubscriptionOutputsDropdown } from './FlowSubscriptionOutputsDropdown';

const NUM_ROWS: number = 30; // not actually configurable for now

const rootClass: string = 'FlowSubscriptionOutputsSelector';

const outputsAreaWrapper = cssClass(rootClass, 'outputsAreaWrapper', {
  paddingTop: TabStyles.Spacing.MediumSpace,
});

const optionsWrapper = cssClass(rootClass, 'optionsWrapper', {
  paddingTop: TabStyles.Spacing.SmallSpace,
  marginLeft: TabStyles.Spacing.ExtraLargeSpace,
});

const topPadding = cssClass(rootClass, 'topPadding', {
  paddingTop: TabStyles.Spacing.DefaultSpace,
});

export interface FlowSubscriptionOutputsSectionProps {
  readonly outputs: ReadonlyArray<FlowOutputStepInfo>;
  readonly selectedOutputForSubscription: string | null;
  readonly shouldShowFirstNRows: boolean;
  readonly shouldAttachOutputDataToEmail: boolean;
  readonly shouldIncludeOutputData: boolean;
  readonly outputFileFormat: OutputFileFormat;
  readonly shouldIncludeLinkToDatasource: boolean;
  readonly onSelectedOutputForSubscriptionChange: (value: string) => void;
  readonly onShowFirstNRowsToggled: () => void;
  readonly onAttachOutputDataToEmailToggled: () => void;
  readonly onIncludeOutputDataToggled: () => void;
  readonly onOutputFileFormatChange: (value: string) => void;
  readonly onIncludeLinkToDatasourceToggled: () => void;
}

function renderFirstNRowsCheckbox(props: FlowSubscriptionOutputsSectionProps): React.ReactNode {
  return (
    <CheckBoxWithLabelWidget
      label={Messages.flowSubscriptionsFirstNRows({ROW_COUNT_LOC: Formatters.formatCount(NUM_ROWS)})}
      checked={props.shouldShowFirstNRows}
      handleChange={props.onShowFirstNRowsToggled}
      testId={'flow-subscription-include-first-n-rows'}
      style={{marginLeft: 0}}
    />
  );
}

function renderAttachOutputDataToEmailCheckboxWithDropdown(props: FlowSubscriptionOutputsSectionProps): React.ReactNode {
  return (
    <>
      <CheckBoxWithLabelWidget
        label={Messages.flowSubscriptionsAttachOutputDataToEmail()}
        checked={props.shouldAttachOutputDataToEmail}
        handleChange={props.onAttachOutputDataToEmailToggled}
        testId={'flow-subscription-attach-data-to-email'}
        style={{marginLeft: 0}}
      />
      <div className={optionsWrapper}>
        <FlowSubscriptionOutputFileFormatDropdown
          value={props.outputFileFormat}
          onChange={props.onOutputFileFormatChange}
          disabled={!props.shouldAttachOutputDataToEmail}
        />
      </div>
    </>
  );
}

function renderPublishedDatasourceOptions(props: FlowSubscriptionOutputsSectionProps, isDataInEmailBodyEnabled: boolean, isDataAsEmailAttachmentEnabled: boolean): React.ReactNode {
  return (
    <div className={classes(optionsWrapper, topPadding)} data-tb-test-id={'published-ds-options-test-id'}>
      {isDataInEmailBodyEnabled && renderFirstNRowsCheckbox(props)}
      <CheckBoxWithLabelWidget
        label={Messages.flowSubscriptionsIncludeLinkToDataSource()}
        checked={props.shouldIncludeLinkToDatasource}
        handleChange={props.onIncludeLinkToDatasourceToggled}
        testId={'flow-subscription-include-link-to-datasource'}
        style={{marginLeft: 0}}
      />
      {isDataAsEmailAttachmentEnabled && renderAttachOutputDataToEmailCheckboxWithDropdown(props)}
    </div>
  );
 }

function renderNonPublishedDatasourceOptions(props: FlowSubscriptionOutputsSectionProps, isDataInEmailBodyEnabled: boolean, isDataAsEmailAttachmentEnabled: boolean): React.ReactNode {

  return (
    <div className={classes(optionsWrapper, topPadding)} data-tb-test-id={'non-published-ds-options-test-id'}>
      {isDataInEmailBodyEnabled && renderFirstNRowsCheckbox(props)}
      {isDataAsEmailAttachmentEnabled && renderAttachOutputDataToEmailCheckboxWithDropdown(props)}
    </div>
  );
}

function renderOnPremOptions(props: FlowSubscriptionOutputsSectionProps, isDataInEmailBodyEnabled: boolean, isDataAsEmailAttachmentEnabled: boolean): React.ReactNode {
  return (
    <>
      <CheckBoxWithLabelWidget
        label={Messages.flowSubscriptionsIncludeOutputData()}
        checked={props.shouldIncludeOutputData}
        handleChange={props.onIncludeOutputDataToggled}
        testId={'flow-subscription-include-output-data'}
        disabled={props.outputs === null || props.outputs.length < 1}
        style={{marginLeft: 0}}
      />
      <div className={optionsWrapper}>
        <FlowSubscriptionOutputsDropdown
          disableDropdown={!props.shouldIncludeOutputData}
          outputs={props.outputs}
          onChange={props.onSelectedOutputForSubscriptionChange}
          value={props.selectedOutputForSubscription}
          disableCSV={!isDataInEmailBodyEnabled && !isDataAsEmailAttachmentEnabled} // if both site settings are disabled, disable CSV
        />
      </div>
      { props.selectedOutputForSubscription !== null && props.shouldIncludeOutputData ?
        isSelectedOutputAPublishedDatasource(props.selectedOutputForSubscription, props.outputs) ?
        renderPublishedDatasourceOptions(props, isDataInEmailBodyEnabled, isDataAsEmailAttachmentEnabled)
      : renderNonPublishedDatasourceOptions(props, isDataInEmailBodyEnabled, isDataAsEmailAttachmentEnabled)
      : null
      }
    </>
  );
}

function renderOnlineOptions(props: FlowSubscriptionOutputsSectionProps): React.ReactNode {
  return (
    <>
      <CheckBoxWithLabelWidget
        label={Messages.flowSubscriptionsIncludeLinkToPublishedDataSource()}
        checked={props.shouldIncludeLinkToDatasource}
        handleChange={props.onIncludeLinkToDatasourceToggled}
        testId={'flow-subscription-include-link-to-published-ds'}
        disabled={props.outputs === null || props.outputs.length < 1}
        style={{marginLeft: 0}}
      />
      <div className={optionsWrapper}>
        <FlowSubscriptionOutputsDropdown
          disableDropdown={!props.shouldIncludeLinkToDatasource}
          outputs={props.outputs}
          onChange={props.onSelectedOutputForSubscriptionChange}
          value={props.selectedOutputForSubscription}
          isOnlineEnvironment={true}
          disableCSV={true}
        />
      </div>
    </>
  );
}

export const FlowSubscriptionOutputsSection: React.FC<FlowSubscriptionOutputsSectionProps> = (props: FlowSubscriptionOutputsSectionProps) => {
  const appState: AppState = getAppStore().getState();
  const isOnlineEnvironment: boolean = SessionServerSelectors.isOnlineEnvironment(appState);
  const isDataInEmailBodyEnabled = SessionSiteSelectors.isFlowOutputSubscriptionsDataInEmailBodyEnabled(appState);
  const isDataAsEmailAttachmentEnabled = SessionSiteSelectors.isFlowOutputSubscriptionsDataAsEmailAttachmentEnabled(appState);
  return (
    <div className={outputsAreaWrapper}>
      {isOnlineEnvironment ?
        renderOnlineOptions(props)
        : renderOnPremOptions(props, isDataInEmailBodyEnabled, isDataAsEmailAttachmentEnabled)
      }
    </div>
  );
};
